/**
 * FIPE do veículo: valor corrente + HISTÓRICO mês a mês (a FIPE muda mensalmente). O botão consulta/
 * atualiza sob demanda — navega marca→modelo→ano na tabela e traz os últimos meses. Sem marca/modelo
 * a consulta não resolve, então o botão avisa. Best-effort no backend; aqui buscamos o histórico e
 * disparamos a atualização.
 */
import { RefreshCw, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type VeiculoFipe = {
  id: string;
  marca?: string | null;
  modelo?: string | null;
};

type LinhaFipe = { mes_referencia: string; valor: number };

function reais(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function PainelFipe({
  item,
  recarregar,
}: {
  item: VeiculoFipe;
  recarregar: () => Promise<void>;
}) {
  const [historico, setHistorico] = useState<LinhaFipe[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const temDados = !!item.marca && !!item.modelo;

  async function carregar() {
    const r = await api.get<LinhaFipe[]>(`/cadastro/veiculos/${item.id}/fipe/historico`);
    setCarregando(false);
    if (r.ok) setHistorico(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  async function atualizar() {
    if (atualizando) return;
    setAtualizando(true);
    const aviso = toast.loading('Consultando a tabela FIPE…');
    const r = await api.post<{ atualizado: boolean; meses_novos: number }>(
      `/cadastro/veiculos/${item.id}/fipe`,
    );
    toast.dismiss(aviso);
    setAtualizando(false);
    if (!r.ok) return void toast.error(r.erro);
    if (r.dados.atualizado || historico.length > 0) {
      toast.success(
        r.dados.meses_novos > 0
          ? `FIPE atualizada (+${r.dados.meses_novos} mês(es)).`
          : 'FIPE já estava em dia.',
      );
      await carregar();
      await recarregar();
    } else {
      toast('Não encontrei o veículo na FIPE — confira marca, modelo e ano.');
    }
  }

  const atual = historico[0];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="size-4 text-accent" />
          Valor FIPE
        </CardTitle>
        <Button
          variant="secundario"
          size="sm"
          onClick={() => void atualizar()}
          disabled={atualizando || !temDados}
          title={temDados ? 'Consultar a FIPE agora' : 'Preencha marca e modelo primeiro'}
        >
          <RefreshCw className={atualizando ? 'animate-spin' : undefined} />
          {historico.length ? 'Atualizar' : 'Consultar FIPE'}
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {atual && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[22px] font-medium text-text">{reais(atual.valor)}</span>
            <span className="tipo-corpo-pequeno text-text-tertiary">{atual.mes_referencia}</span>
          </div>
        )}

        {carregando ? (
          <Spinner />
        ) : historico.length === 0 ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            {temDados
              ? 'Ainda sem histórico FIPE. Clique em “Consultar FIPE” para buscar pela marca/modelo/ano.'
              : 'Preencha marca e modelo do veículo para consultar a FIPE.'}
          </p>
        ) : (
          <div>
            <span className="tipo-rotulo text-text-tertiary">Histórico mês a mês</span>
            <div className="mt-1 divide-y divide-border overflow-hidden rounded-md border border-border">
              {historico.map((h, i) => {
                const anterior = historico[i + 1];
                const delta = anterior ? h.valor - anterior.valor : 0;
                return (
                  <div
                    key={h.mes_referencia}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="tipo-corpo-pequeno text-text-secondary">
                      {h.mes_referencia}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="tipo-dado text-text">{reais(h.valor)}</span>
                      {anterior && delta !== 0 && (
                        <span
                          className={
                            'tipo-corpo-pequeno ' +
                            (delta > 0 ? 'text-success' : 'text-danger')
                          }
                        >
                          {delta > 0 ? '▲' : '▼'} {reais(Math.abs(delta))}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
